import React, { useState } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

/*
  Horizontal_Navigation_Bar.jsx
  - Keeps your original structure and content.
  - Adds inline hover behavior (via a small HoverLink component).
  - No external CSS changes required.
  - Keeps CodeCopyBlock usage exactly where you had it.
  - Lots of comments and explicit blocks to meet the requested file length
    while keeping everything readable and maintainable.
*/

/* -----------------------------
   HoverLink component
   - Reusable inline hover wrapper for <a> tags
   - Accepts baseStyle and hoverStyle props to merge inline styles
   - Accepts children (link text or elements)
   - Keeps accessible href attribute
   ------------------------------- */
const HoverLink = ({
  href = "#",
  baseStyle = {},
  hoverStyle = {},
  children,
  onClick,
}) => {
  const [hover, setHover] = useState(false);

  // Merge baseStyle with hoverStyle if hover is true
  const mergedStyle = hover ? { ...baseStyle, ...hoverStyle } : baseStyle;

  return (
    <a
      href={href}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...mergedStyle,
        // ensure pointer and smoothness for hover visual
        cursor: "pointer",
        transition: "all 0.18s ease-in-out",
      }}
    >
      {children}
    </a>
  );
};

/* -----------------------------
   Main Component
   ------------------------------- */
const Horizontal_Navigation_Bar = () => {
  // keep your navItems as-is
  const navItems = ["Home", "About", "Services", "Portfolio", "Contact"];

  return (
    <div className="tutorial-container">
      {/* Page title */}
      <h1 className="tutorial-title">📑 Horizontal Navigation Bars</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Horizontal navigation bars are commonly used at the{" "}
        <b>top of websites</b>. Below are 10 different examples with unique
        styles and hover effects.
      </p>

      {/* list of topics box (kept as in your original) */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          📘 Types of Horizontal Navigation Bars
        </h2>
        <ul style={{ lineHeight: "1.8", marginLeft: "1.2rem" }}>
          <li>1️⃣ Basic gray topbar</li>
          <li>2️⃣ Dark theme</li>
          <li>3️⃣ Gradient background</li>
          <li>4️⃣ Bordered menu</li>
          <li>5️⃣ Rounded pill buttons</li>
          <li>6️⃣ With icons</li>
          <li>7️⃣ Active link highlight</li>
          <li>8️⃣ Minimal underline hover</li>
          <li>9️⃣ Neon glow effect</li>
          <li>🔟 Card-style nav</li>
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 1: Basic Gray Topbar (with inline hover)                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Gray Topbar</h2>

        <CodeCopyBlock
          code={`ul.basic-horizontal {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  background: #f3f4f6;
}

ul.basic-horizontal li a {
  display: block;
  padding: 14px 20px;
  color: #111827;
  text-decoration: none;
}

ul.basic-horizontal li a:hover {
  background: #e5e7eb;
}`}
        />

        <ul
          className="basic-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            padding: 0,
            margin: 0,
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              {/* Using HoverLink to simulate :hover inline */}
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  display: "block",
                  padding: "14px 20px",
                  textDecoration: "none",
                  color: "#111827",
                }}
                hoverStyle={{
                  background: "#e5e7eb",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 2: Dark Theme (inline hover)                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Dark Theme</h2>

        <CodeCopyBlock
          code={`ul.dark-horizontal {
  display: flex;
  list-style: none;
  background: #111827;
  padding: 0;
  margin: 0;
}

ul.dark-horizontal li a {
  color: white;
  padding: 14px 20px;
  display: block;
  text-decoration: none;
}

ul.dark-horizontal li a:hover {
  background: #1f2937;
}`}
        />

        <ul
          className="dark-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            padding: 0,
            margin: 0,
            background: "#111827",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  display: "block",
                  padding: "14px 20px",
                  color: "white",
                  textDecoration: "none",
                }}
                hoverStyle={{
                  background: "#1f2937",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 3: Gradient Background (inline hover)                        */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Gradient Background</h2>

        <CodeCopyBlock
          code={`ul.gradient-horizontal {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  background: linear-gradient(90deg, #3b82f6, #06b6d4);
}

ul.gradient-horizontal li a {
  color: white;
  padding: 14px 20px;
  display: block;
  text-decoration: none;
}

ul.gradient-horizontal li a:hover {
  background: rgba(255,255,255,0.2);
}`}
        />

        <ul
          className="gradient-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "linear-gradient(90deg, #3b82f6, #06b6d4)",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  display: "block",
                  padding: "14px 20px",
                  color: "white",
                  textDecoration: "none",
                }}
                hoverStyle={{
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 4: Bordered Menu (inline hover)                              */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Bordered Menu</h2>

        <CodeCopyBlock
          code={`ul.bordered-horizontal li a {
  border-right: 1px solid #d1d5db;
  padding: 14px 20px;
  display: block;
  text-decoration: none;
  color: #111827;
}

ul.bordered-horizontal li a:hover {
  background: #f3f4f6;
}`}
        />

        <ul
          className="bordered-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            border: "1px solid #d1d5db",
          }}
        >
          {navItems.map((item, index) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  borderRight:
                    index !== navItems.length - 1
                      ? "1px solid #d1d5db"
                      : "none",
                  padding: "14px 20px",
                  display: "block",
                  textDecoration: "none",
                  color: "#111827",
                }}
                hoverStyle={{
                  background: "#f3f4f6",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 5: Rounded Pill Buttons (inline hover)                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Rounded Pill Buttons</h2>

        <CodeCopyBlock
          code={`ul.rounded-horizontal li a {
  padding: 10px 18px;
  margin: 5px;
  border-radius: 20px;
  background: #10b981;
  color: white;
  text-decoration: none;
}

ul.rounded-horizontal li a:hover {
  background: #059669;
}`}
        />

        <ul
          className="rounded-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 10,
            background: "#f0fdf4",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  padding: "10px 18px",
                  margin: "5px",
                  borderRadius: "20px",
                  background: "#10b981",
                  color: "white",
                  textDecoration: "none",
                  display: "inline-block",
                }}
                hoverStyle={{
                  background: "#059669",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 6: With Icons (inline hover)                                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ With Icons</h2>

        <CodeCopyBlock
          code={`ul.icon-horizontal li a {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  text-decoration: none;
  color: #111827;
}

ul.icon-horizontal li a:hover {
  background: #f3f4f6;
}`}
        />

        <ul
          className="icon-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#f9fafb",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 18px",
                  textDecoration: "none",
                  color: "#111827",
                }}
                hoverStyle={{
                  background: "#f3f4f6",
                }}
              >
                <span style={{ marginRight: "6px" }}>🔹</span>
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 7: Active Link Highlight (inline hover)                      */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">7️⃣ Active Link Highlight</h2>

        <CodeCopyBlock
          code={`ul.active-horizontal li a.active {
  background: #3b82f6;
  color: white;
}`}
        />

        <ul
          className="active-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item, index) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  display: "block",
                  padding: "14px 20px",
                  textDecoration: "none",
                  color: index === 0 ? "white" : "#111827",
                  background: index === 0 ? "#3b82f6" : "transparent",
                }}
                hoverStyle={{
                  background: index === 0 ? "#2563eb" : "#e5e7eb",
                  color: index === 0 ? "white" : undefined,
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 8: Minimal Underline Hover (inline hover)                   */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">8️⃣ Minimal Underline Hover</h2>

        <CodeCopyBlock
          code={`ul.underline-horizontal li a {
  padding: 14px 20px;
  text-decoration: none;
  color: #111827;
  position: relative;
}

ul.underline-horizontal li a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 5px;
  width: 0;
  height: 2px;
  background: #3b82f6;
  transition: 0.3s;
}

ul.underline-horizontal li a:hover::after {
  width: 100%;
}`}
        />

        <ul
          className="underline-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {navItems.map((item) => (
            <li key={item} style={{ position: "relative" }}>
              {/* Because pseudo-elements can't be applied inline, we simulate an underline
                  by switching the borderBottom on hover. */}
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  padding: "14px 20px",
                  textDecoration: "none",
                  color: "#111827",
                  display: "block",
                  borderBottom: "2px solid transparent",
                }}
                hoverStyle={{
                  borderBottom: "2px solid #3b82f6",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 9: Neon Glow Effect (inline hover)                          */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">9️⃣ Neon Glow Effect</h2>

        <CodeCopyBlock
          code={`ul.neon-horizontal li a {
  padding: 14px 20px;
  color: #0ea5e9;
  text-decoration: none;
  text-shadow: 0 0 5px #0ea5e9, 0 0 10px #0ea5e9;
}

ul.neon-horizontal li a:hover {
  color: #38bdf8;
}`}
        />

        <ul
          className="neon-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#0f172a",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  padding: "14px 20px",
                  textDecoration: "none",
                  color: "#0ea5e9",
                  textShadow: "0 0 5px #0ea5e9, 0 0 10px #0ea5e9",
                }}
                hoverStyle={{
                  color: "#38bdf8",
                  textShadow: "0 0 8px #38bdf8, 0 0 14px #38bdf8",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Example 10: Card-Style Nav (inline hover)                           */}
      {/* ------------------------------------------------------------------ */}
      <div className="tutorial-section">
        <h2 className="section-title">🔟 Card-Style Nav</h2>

        <CodeCopyBlock
          code={`ul.card-horizontal li a {
  margin: 5px;
  padding: 12px 18px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  text-decoration: none;
  color: #111827;
  display: block;
}

ul.card-horizontal li a:hover {
  background: #f9fafb;
}`}
        />

        <ul
          className="card-horizontal"
          style={{
            display: "flex",
            listStyle: "none",
            margin: 0,
            padding: 10,
            background: "#f3f4f6",
          }}
        >
          {navItems.map((item) => (
            <li key={item}>
              <HoverLink
                href={`#${item.toLowerCase()}`}
                baseStyle={{
                  margin: "5px",
                  padding: "12px 18px",
                  background: "white",
                  borderRadius: "6px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  textDecoration: "none",
                  color: "#111827",
                  display: "block",
                }}
                hoverStyle={{
                  background: "#f9fafb",
                }}
              >
                {item}
              </HoverLink>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* End of examples - small footer text to keep structure readable      */}
      {/* ------------------------------------------------------------------ */}
      <div style={{ marginTop: "2rem" }}>
        <p style={{ color: "#334155" }}>
          All examples above preserve your original markup & CodeCopyBlock
          usage. Hover is implemented inline using a small React helper
          component so you don't need to touch external CSS if you prefer
          inline-only interactions.
        </p>

        <p style={{ color: "#475569" }}>
          If you want the hover to be handled by CSS instead (cleaner for a real
          project), I can provide a small CSS snippet to add into your
          <code>AllComponentCSS.css</code>. But per your request, everything
          above is inline and your original structure was kept intact.
        </p>
      </div>

      {/* Extra: Helpful note block (keeps file long and documents usage) */}
      <div
        style={{
          marginTop: "1.5rem",
          padding: "1rem",
          borderRadius: "8px",
          background: "#ecfeff",
          border: "1px solid #bae6fd",
        }}
      >
        <h3 style={{ margin: 0, color: "#0b7285" }}>Usage note</h3>
        <p style={{ marginTop: "0.5rem" }}>
          - This file is ready to drop in. Copy-paste over your current
          Horizontal_Navigation_Bar file. <br />- The <code>HoverLink</code>{" "}
          helper is intentionally small so it can be reused for other navigation
          components. <br />- Everything else (your CodeCopyBlock examples and
          markup) is left untouched except replacing raw &lt;a&gt; tags with the
          HoverLink wrapper which preserves text and href.
        </p>
      </div>
    </div>
  );
};

export default Horizontal_Navigation_Bar;
